import { groupByProp } from "remeda"
import type { RouteLocationNormalizedLoadedGeneric } from "vue-router"
import z from "zod"

// ============================================================================
// SCHEMAS
// ============================================================================

const StaffOrMemberSchema = z.object({
  staffId: z.number().optional(),
  memberId: z.number().optional()
})

const Site = z.object({
  name: z.string(),
  type: z.enum(['School', 'District'])
})

export type Site = z.infer<typeof Site>

const StaffRecipientSchema = z.object({
  $type: z.literal('Staff'),
  staffId: z.number().gt(0),
  position: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  sites: z.array(Site).optional()
})

const MemberRecipientSchema = z.object({
  $type: z.literal('Member'),
  memberId: z.number().gt(0),
  memberPhotoId: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  sites: z.array(Site).optional()
})

type BaseRecipient =
  | z.infer<typeof StaffRecipientSchema>
  | z.infer<typeof MemberRecipientSchema>
  | {
      $type: 'Group',
      groupId: number,
      groupName: string,
      recipients: BaseRecipient[]
    }

const RecipientSchema: z.ZodType<BaseRecipient> = z.lazy(() =>
  z.discriminatedUnion('$type', [
    StaffRecipientSchema,
    MemberRecipientSchema,
    z.object({
      $type: z.literal('Group'),
      groupId: z.number().gt(0),
      groupName: z.string(),
      recipients: z.array(RecipientSchema)
    })
  ])
)

const ConversationErrorSchema = z.object({
  message: z.string(),
  timestamp: z.coerce.date(),
})

export type ConversationError = z.infer<typeof ConversationErrorSchema>

const ConversationAttachmentSchema = z.object({
  fileName: z.string().nonempty(),
  uploadId: z.string().uuid()
})

export type ConversationAttachment = z.infer<typeof ConversationAttachmentSchema>

const ConversationMessageSchema = z.object({
  messageId: z.number().gt(0),
  content: z.string(),
  errors: z.array(ConversationErrorSchema),
  created: z.coerce.date(),
  sender: StaffOrMemberSchema,
  readBy: z.array(StaffOrMemberSchema),
  attachments: z.array(ConversationAttachmentSchema).optional()
})

const ConversationDetailSchema = z.object({
  threadId: z.number(),
  linkedMessage: z.number().optional(),
  regarding: z.string().optional(),
  recipients: z.array(RecipientSchema),
  messages: z.array(ConversationMessageSchema)
})

const ConversationOverviewSchema = z.object({
  threadId: z.number(),
  recipients: z.array(RecipientSchema),
  linkedMessage: z.number().optional(),
  regarding: z.string().optional(),
  previewContent: z.string().optional(),
  lastActivity: z.coerce.date(),
  hasAttachments: z.boolean(),
  hasUnreadMessages: z.boolean(),
})

const SentMessageSchema = z.object({
  eventData: z.object({
    messageId: z.number(),
    content: z.string(),
    attachments: z.array(ConversationAttachmentSchema).optional()
  }),
  id: z.number(),
  threadId: z.number(),
  eventType: z.string(),
  initiator: StaffOrMemberSchema.transform(s => new StaffOrMember(s)),
  timestamp: z.coerce.date(),
  correlationId: z.string(),
  sequenceId: z.number()
})

export type SentMessage = z.infer<typeof SentMessageSchema>

// ============================================================================
// RECIPIENTS
// ============================================================================

export abstract class Recipient {
  abstract readonly $type: 'Staff' | 'Member' | 'Group'
  abstract get idKey(): string
  abstract get idValue(): number | string

  abstract role(): string
  abstract fullName(): string

  isEqual(other: unknown): boolean {
    if (typeof other !== 'object' || other === null) return false

    return (
      this.idKey in other &&
      other[this.idKey as keyof typeof other] === this.idValue
    )
  }

  key(): string {
    return `${this.$type}-${this.idValue}`
  }

  public static Create(data: BaseRecipient) {
    switch (data.$type) {
      case 'Staff': return new StaffRecipient(data)
      case 'Member': return new MemberRecipient(data)
      case 'Group': return new GroupRecipient(data)
    }
  }
}

export class StaffRecipient extends Recipient {
  constructor(data: Extract<BaseRecipient, { $type: 'Staff' }>) {
    super()
    Object.assign(this, data)
  }

  readonly $type = 'Staff' as const
  readonly staffId!: number
  readonly position?: string
  readonly firstName?: string
  readonly lastName?: string
  readonly sites?: Site[]

  get idKey() { return 'staffId' }
  get idValue() { return this.staffId }

  role() {
    return this.position ?? 'Staff'
  }

  fullName() {
    return [this.firstName, this.lastName].filter(Boolean).join(' ')
  }
}

export class MemberRecipient extends Recipient {
  constructor(data: Extract<BaseRecipient, { $type: 'Member' }>) {
    super()
    Object.assign(this, data)
  }

  readonly $type = 'Member' as const
  readonly memberId!: number
  readonly memberPhotoId?: string
  readonly firstName!: string
  readonly lastName!: string
  readonly sites?: Site[]

  get idKey() { return 'memberId' }
  get idValue() { return this.memberId }

  role() {
    return 'Student Contact'
  }

  fullName() {
    return `${this.firstName} ${this.lastName}`
  }
}

export class GroupRecipient extends Recipient {
  readonly $type = 'Group' as const
  readonly groupId: number
  readonly groupName: string
  readonly recipients: Recipient[]

  constructor(data: Extract<BaseRecipient, { $type: 'Group' }>) {
    super()
    this.groupId = data.groupId
    this.groupName = data.groupName
    this.recipients = data.recipients.map(Recipient.Create)
  }

  get idKey() { return 'groupId' }
  get idValue() { return this.groupId }

  role() {
    return 'N/A'
  }

  fullName() {
    return this.groupName
  }
}

// ============================================================================
// STAFF OR MEMBER
// ============================================================================

export class StaffOrMember {
  constructor(data: z.infer<typeof StaffOrMemberSchema>) {
    Object.assign(this, data)
  }

  readonly staffId?: number
  readonly memberId?: number

  isEqual(other: unknown): boolean {
    if (!other || typeof other !== 'object') return false

    return (
      ('memberId' in other && this.memberId === other.memberId) ||
      ('staffId' in other && this.staffId === other.staffId)
    )
  }
}

// ============================================================================
// DATE FORMATTING UTILITY
// ============================================================================

class RelativeDateFormatter {
  private static readonly formats = {
    sameDay: new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }),
    thisWeek: new Intl.DateTimeFormat(undefined, { weekday: 'short' }),
    thisYear: new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }),
    yearsPast: new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  }

  static format(date: Date): string {
    const now = new Date()
    if (now < date) return 'Just Now'

    const diff = now.getTime() - date.getTime()
    const oneDay = 24 * 60 * 60 * 1000

    if (now.toDateString() === date.toDateString()) {
      return this.formats.sameDay.format(date)
    }

    if (diff < 7 * oneDay) {
      return this.formats.thisWeek.format(date)
    }

    if (now.getFullYear() === date.getFullYear()) {
      return this.formats.thisYear.format(date)
    }

    return this.formats.yearsPast.format(date)
  }
}

// ============================================================================
// CONVERSATION OVERVIEW
// ============================================================================

export class ConversationOverview {
  constructor(data: z.infer<typeof ConversationOverviewSchema>) {
    this.threadId = data.threadId
    this.recipients = data.recipients.map(Recipient.Create)
    this.linkedMessage = data.linkedMessage
    this.regarding = data.regarding
    this.previewContent = data.previewContent
    this.lastActivity = data.lastActivity
    this.hasAttachments = data.hasAttachments
    this.hasUnreadMessages = data.hasUnreadMessages
  }

  readonly threadId: number
  readonly recipients: Recipient[]
  readonly linkedMessage?: number
  readonly regarding?: string
  readonly previewContent?: string
  readonly lastActivity: Date
  readonly hasAttachments: boolean
  readonly hasUnreadMessages: boolean

  formatLastActivity() {
    return RelativeDateFormatter.format(this.lastActivity)
  }

  displayName(viewer: unknown) {
    return this.regarding ?? this.formatRecipients(viewer)
  }

  formatRecipients(viewer: unknown) {
    const others = this.recipients.filter(r => !r.isEqual(viewer))

    if (others.length === 0) return ''
    if (others.length === 1) return others[0].fullName()
    if (others.length === 2) return others.map(r => r.fullName()).join(', ')

    return `${others[0].fullName()} +${others.length - 1}`
  }

  listRecipients() {
    return this.recipients.map(r => r.fullName()).join(', ')
  }
}

// ============================================================================
// CONVERSATION MESSAGE
// ============================================================================

export class ConversationMessage {
  private static readonly TimeFormat = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })

  constructor(data: z.infer<typeof ConversationMessageSchema>) {
    this.messageId = data.messageId
    this.content = data.content
    this.errors = data.errors
    this.created = data.created
    this.sender = new StaffOrMember(data.sender)
    this.readBy = data.readBy.map(s => new StaffOrMember(s))
    this.attachments = data.attachments
  }

  readonly messageId: number
  readonly content: string
  readonly errors: ConversationError[]
  readonly created: Date
  readonly sender: StaffOrMember
  readonly readBy: StaffOrMember[]
  readonly attachments?: ConversationAttachment[]

  isIncoming(viewer: object) {
    return !this.isOutgoing(viewer)
  }

  isOutgoing(viewer: object) {
    return this.sender.isEqual(viewer)
  }

  paragraphs() {
    return this.content.split('\n\n')
  }

  get dayCreated() {
    return this.created.getDate()
  }

  get dayCreatedFormatted() {
    const today = new Date()
    const yesterday = new Date()
    yesterday.setDate(today.getDate() - 1)

    if (this.created.toDateString() === today.toDateString()) return "Today"
    if (this.created.toDateString() === yesterday.toDateString()) return "Yesterday"

    return new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: this.created.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    }).format(this.created)
  }

  createdTime() {
    return ConversationMessage.TimeFormat.format(this.created)
  }
}

// ============================================================================
// CONVERSATION DETAIL
// ============================================================================

export class ConversationDetail {
  constructor(data: z.infer<typeof ConversationDetailSchema>) {
    this.threadId = data.threadId
    this.linkedMessage = data.linkedMessage
    this.regarding = data.regarding
    this.recipients = data.recipients.map(Recipient.Create)
    this.messages = data.messages.map(m => new ConversationMessage(m))
  }

  readonly threadId: number
  readonly linkedMessage?: number
  readonly regarding?: string
  readonly recipients: Recipient[]
  readonly messages: ConversationMessage[]

  groupByDay() {
    return groupByProp(this.messages, 'dayCreatedFormatted')
  }

  formatSender(message: ConversationMessage) {
    return this.recipients.find(r => r.isEqual(message.sender))?.fullName() ?? 'Unknown'
  }

  recipientsExcept(recipient: object) {
    return this.recipients.filter(r => !r.isEqual(recipient))
  }

  recipientNamesExcept(recipient: object) {
    return this.recipientsExcept(recipient).map(r => r.fullName())
  }

  getLastUnreadMessage(other: object) {
    // Sending or reading a message implies all previous messages have been read,
    // so only the last incoming message can be unread.
    const lastIncoming = this.findLastIncomingMessage(other)

    if (lastIncoming && !lastIncoming.readBy.some(r => r.isEqual(other)))
      return lastIncoming

    return undefined
  }

  private findLastIncomingMessage(viewer: object) {
    for (let i = this.messages.length - 1; 0 <= i; i--) {
      if (!this.messages[i].sender.isEqual(viewer))
        return this.messages[i]
    }

    return undefined
  }

  static GetThreadIdFromQueryParams(route: RouteLocationNormalizedLoadedGeneric) {
    const result = z.coerce.number().safeParse(route.query.thread?.valueOf() ?? route.query.draft?.valueOf())
    return result.success ? result.data : undefined
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export const conversationSchemas = {
  ConversationAttachmentSchema,
  ConversationDetailSchema,
  ConversationErrorSchema,
  ConversationMessageSchema,
  ConversationOverviewSchema,
  MemberRecipientSchema,
  RecipientSchema,
  SentMessageSchema,
  StaffRecipientSchema,
  StaffOrMemberSchema
}
